/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { CircularProgress, Paper } from '@mui/material';
import LegacyGrid from './legacy/LegacyGrid';
import * as React from 'react';
import { Category } from '../common/Categories';
import { sendRequestAndReceiveResponse } from '../common/common';
import { fetchAuthorizationsEndpoint } from '../common/constants';
import { GridGraphqlExtras, isGridGraphqlExtras, toAuthorizationGraphqlBody } from '../common/graphqlJsonBody';
import DataGrid from './DataGrid';

export const dataTableRenderer = (classes: any, initialLoading: boolean, selectedCategory: Category | undefined, rows: any,
    totalRows: number, refreshChart: Function, loadData: Function, handleExport: Function, isLoading: boolean, norecords = false
) => {
    return (
        <LegacyGrid item={true} className={classes.table}>
            {initialLoading ?
                (
                    <div className={classes.progressWrap}>
                        <CircularProgress className={classes.progress} color="secondary" />
                    </div>
                ) :
                norecords ?
                    <Paper style={
                        {
                            borderRadius: '0px',
                            textAlign: 'center' as const,
                            padding: '20px',
                            marginTop: '20px'
                        }}>No data available</Paper>
                    :
                    <DataGrid
                        selectedCategory={selectedCategory}
                        data={rows}
                        totalRows={totalRows}
                        refreshChart={refreshChart}
                        loadData={loadData}
                        exportData={handleExport}
                        isloading={isLoading}
                    />
            }
        </LegacyGrid>
    );
};

export const loadTableData = async (queryOrExtras: string | GridGraphqlExtras, selectedCategory: Category, isExport?: boolean) => {
    console.log('loadTableData - endpoint:', fetchAuthorizationsEndpoint, 'queryOrExtras:', queryOrExtras);
    if (selectedCategory !== undefined) {
        const body = isGridGraphqlExtras(queryOrExtras)
            ? toAuthorizationGraphqlBody(selectedCategory.requestBodyForGrid, selectedCategory.id, queryOrExtras)
            : toAuthorizationGraphqlBody(queryOrExtras, selectedCategory.id);
        const gridData = await sendRequestAndReceiveResponse(
            body,
            fetchAuthorizationsEndpoint);
        console.log('loadTableData - received gridData:', gridData);
        const rows: any = [];
        switch (selectedCategory.id) {
            case 8:
                gridData !== undefined && gridData.data !== undefined && gridData.data.trainings !== undefined && gridData.data.trainings.data !== undefined &&
                    gridData.data.trainings.data.length && gridData.data.trainings.data.map((x: any) => {
                        rows.push({
                            id: x.companyContactTwoDproductModelId,
                            twodproductmodel: x.twodproductmodel.name,
                            coursename: x.coursename,
                            certificationname: x.certificationname,
                            dateoftraining: x.dateOfTraining !== undefined ? new Date(x.dateOfTraining + 'Z').toLocaleDateString() : '',
                            iscertified: x.iscertified ? 'Yes' : 'No',
                            isactive: x.isactive ? 'Yes' : 'No'
                        });
                    });
                return { rows, totalRows: gridData !== undefined && gridData.data !== undefined && gridData.data.trainings !== undefined ? gridData.data.trainings.count : 0 };
            case 9:
                gridData !== undefined && gridData.data !== undefined && gridData.data.trainings !== undefined &&
                    gridData.data.trainings.data !== undefined && gridData.data.trainings.data.length && gridData.data.trainings.data.map((x: any) => {
                        rows.push({
                            id: x.companyContactTwoDproductModelId,
                            name: `${x.companycontact.firstname} ${x.companycontact.lastname}`,
                            email: x.companycontact.email,
                            twodproductmodel: x.twodproductmodel.name,
                            coursename: x.coursename,
                            certificationname: x.certificationname,
                            dateoftraining: x.dateOfTraining !== undefined ? new Date(x.dateOfTraining + 'Z').toLocaleDateString() : '',
                            iscertified: x.iscertified ? 'Yes' : 'No',
                            isactive: x.isactive ? 'Yes' : 'No'
                        });
                    });
                return { rows, totalRows: gridData !== undefined && gridData.data !== undefined && gridData.data.trainings !== undefined ? gridData.data.trainings.count : 0 };
            case 10:
                gridData !== undefined && gridData.data !== undefined && gridData.data.productauthorizations !== undefined &&
                    gridData.data.productauthorizations.data !== undefined && gridData.data.productauthorizations.data.length && gridData.data.productauthorizations.data.map((x: any) => {
                        rows.push({
                            id: x.twodproductmodelname,
                            twodproductname: x.twodproductname,
                            twodproductmodelname: x.twodproductmodelname,
                            twodproductfamilyname: x.twodproductfamilyname
                        });
                    });
                return { rows, totalRows: gridData !== undefined && gridData.data !== undefined && gridData.data.productauthorizations !== undefined ? gridData.data.productauthorizations.count : 0 };
            case 11:
                if (isExport) {
                    gridData !== undefined && gridData.data !== undefined && gridData.data.allTraining !== undefined && gridData.data.allTraining.data !== undefined
                        && gridData.data.allTraining.data.length &&
                        gridData.data.allTraining.data.map((x: any) => {
                            rows.push({
                                id: `${x.email} ${x.twodProductModel}`,
                                name: `${x.firstName} ${x.lastName}`,
                                email: x.email,
                                twodproductmodel: x.twodProductModel,
                                coursename: x.coursename,
                                certificationname: x.certificationname,
                                dateoftraining: x.dateOfTraining !== undefined ? new Date(x.dateOfTraining + 'Z').toLocaleDateString() : '',
                                company: x.companyname,
                                iscertified: x.iscertified ? 'Yes' : 'No',
                                isactive: x.isActive ? 'Yes' : 'No'
                            });
                        });
                    return { rows, totalRows: gridData !== undefined && gridData.data !== undefined && gridData.data.allTraining !== undefined ? gridData.data.allTraining.count : 0 };
                }
                else {
                    gridData !== undefined && gridData.data !== undefined && gridData.data.trainings !== undefined && gridData.data.trainings.data !== undefined
                        && gridData.data.trainings.data.length &&
                        gridData.data.trainings.data.map((x: any) => {
                            rows.push({
                                id: x.companyContactTwoDproductModelId,
                                name: `${x.companycontact.firstname} ${x.companycontact.lastname}`,
                                email: x.companycontact.email,
                                twodproductmodel: x.twodproductmodel.name,
                                coursename: x.coursename,
                                certificationname: x.certificationname,
                                company: x.companycontact.company !== undefined ? x.companycontact.company.companyname : '',
                                dateoftraining: x.dateOfTraining !== undefined ? new Date(x.dateOfTraining + 'Z').toLocaleDateString() : '',
                                iscertified: x.iscertified ? 'Yes' : 'No',
                                isactive: x.isactive ? 'Yes' : 'No'
                            });
                        });
                    return { rows, totalRows: gridData !== undefined && gridData.data !== undefined && gridData.data.trainings !== undefined ? gridData.data.trainings.count : 0 };
                }
            case 12:
                if (isExport) {
                    gridData !== undefined && gridData.data !== undefined && gridData.data.allProductAuthorization !== undefined && gridData.data.allProductAuthorization.data !== undefined &&
                        gridData.data.allProductAuthorization.data.length &&
                        gridData.data.allProductAuthorization.data.map((x: any) => {
                            rows.push({
                                id: `${x.companyname} ${x.twodProductModelName}`,
                                companyname: x.companyname,
                                twodproductmodelname: x.twodProductModelName,
                                twodproductname: x.twodProductName,
                                twodproductfamilyname: x.twodProductFamily
                            });
                        });
                    return { rows, totalRows: gridData !== undefined && gridData.data !== undefined && gridData.data.allProductAuthorization !== undefined ? gridData.data.allProductAuthorization.count : 0 };
                }
                else {
                    gridData !== undefined && gridData.data !== undefined && gridData.data.productauthorizations !== undefined && gridData.data.productauthorizations.data !== undefined &&
                        gridData.data.productauthorizations.data.length &&
                        gridData.data.productauthorizations.data.map((x: any) => {
                            rows.push({
                                id: `${x.companyid} ${x.twodproductmodelname}`,
                                companyid: x.companyid,
                                companyname: x.companyname,
                                twodproductname: x.twodproductname,
                                twodproductmodelname: x.twodproductmodelname,
                                twodproductfamilyname: x.twodproductfamilyname
                            });
                        });
                    return { rows, totalRows: gridData !== undefined && gridData.data !== undefined && gridData.data.productauthorizations !== undefined ? gridData.data.productauthorizations.count : 0 };
                }
        }
    }
};

export const getExportQueryFromGridRequest = (requestBodyForGrid: string) => {
    return requestBodyForGrid
        .replace(/\s*,\s*%s/g, ')')
        .replace(/\(\s*%s/g, '')
        .replace(/%s/g, '');
};
